/**
 * TypeCalculator - Calculate weaknesses and resistances
 */
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, Ban } from 'lucide-react';
import { TypeBadge } from './TypeBadge';
import { useApp } from '../../context/AppContext';
import { TYPE_CHART } from '../../hooks/usePokemon';

// Calculate combined type effectiveness
function calculateEffectiveness(types) {
  const effectiveness = {};
  
  // All types start at 1x
  Object.keys(TYPE_CHART).forEach(type => {
    effectiveness[type] = 1;
  });
  
  types.forEach(pokemonType => {
    const typeData = TYPE_CHART[pokemonType];
    if (!typeData) return;
    
    // Apply weaknesses (2x)
    typeData.weakTo.forEach(type => {
      effectiveness[type] *= 2;
    });
    
    // Apply resistances (0.5x)
    typeData.resistantTo.forEach(type => {
      effectiveness[type] *= 0.5;
    });
    
    // Apply immunities (0x)
    typeData.immuneTo.forEach(type => {
      effectiveness[type] = 0;
    });
  });
  
  return effectiveness;
}

export function TypeCalculator({ types }) {
  const { t } = useApp();
  
  const effectiveness = useMemo(() => {
    return calculateEffectiveness(types);
  }, [types]);
  
  const categorized = useMemo(() => {
    const result = {
      immune: [],      // 0x
      quadResist: [],  // 0.25x
      resist: [],      // 0.5x
      neutral: [],     // 1x
      weak: [],        // 2x
      quadWeak: []     // 4x
    };
    
    Object.entries(effectiveness).forEach(([type, value]) => {
      if (value === 0) result.immune.push(type);
      else if (value === 0.25) result.quadResist.push(type);
      else if (value === 0.5) result.resist.push(type);
      else if (value === 1) result.neutral.push(type);
      else if (value === 2) result.weak.push(type);
      else if (value >= 4) result.quadWeak.push(type);
    });
    
    return result;
  }, [effectiveness]);
  
  return (
    <div className="space-y-4" data-testid="type-calculator">
      {/* Weaknesses (4x) */}
      {categorized.quadWeak.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold text-red-600 dark:text-red-400">
              {t('weaknesses')} (4x)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categorized.quadWeak.map(type => (
              <TypeBadge key={type} type={type} size="sm" />
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Weaknesses (2x) */}
      {categorized.weak.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
              {t('weaknesses')} (2x)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categorized.weak.map(type => (
              <TypeBadge key={type} type={type} size="sm" />
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Resistances (0.5x) */}
      {categorized.resist.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
              {t('resistances')} (0.5x)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categorized.resist.map(type => (
              <TypeBadge key={type} type={type} size="sm" />
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Resistances (0.25x) */}
      {categorized.quadResist.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
              {t('resistances')} (0.25x)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categorized.quadResist.map(type => (
              <TypeBadge key={type} type={type} size="sm" />
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Immunities */}
      {categorized.immune.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2">
            <Ban className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold text-violet-600 dark:text-violet-400">
              {t('immunities')} (0x)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categorized.immune.map(type => (
              <TypeBadge key={type} type={type} size="sm" />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default TypeCalculator;
